/**
 * Account Settings Page
 *
 * Displays user account information and subscription management.
 * Allows users to view their subscription status and cancel if needed.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UserCircleIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

export default function AccountSettings() {
  const navigate = useNavigate();
  const { currentUser, userProfile, fetchUserProfile, getToken } = useAuth();

  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch subscription status on mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await api.get('/subscriptions/status');
        setSubscriptionStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch subscription status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    setMessage(null);

    try {
      const response = await api.post('/subscriptions/cancel');

      if (response.data.success) {
        const cancelDate = response.data.cancel_at
          ? new Date(response.data.cancel_at * 1000).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })
          : 'the end of your billing period';

        setMessage({
          type: 'success',
          text: `Your subscription has been cancelled. You'll retain premium access until ${cancelDate}.`,
        });

        // Refresh subscription status
        const statusResponse = await api.get('/subscriptions/status');
        setSubscriptionStatus(statusResponse.data);

        // Refresh user profile
        const token = await getToken();
        if (token) {
          await fetchUserProfile(token);
        }
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to cancel subscription. Please try again or contact support.',
      });
    } finally {
      setCancelLoading(false);
      setShowCancelModal(false);
    }
  };

  const isPremium = userProfile?.subscription_tier === 'premium' || userProfile?.subscription_tier === 'pro';
  const memberSince = userProfile?.created_at
    ? new Date(userProfile.created_at).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      })
    : 'N/A';

  return (
    <div className="min-h-screen bg-bg-primary p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-text-secondary hover:text-text-primary mb-4 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>
        <h1 className="text-3xl font-bold text-text-primary">Account Settings</h1>
        <p className="text-text-secondary mt-1">Manage your account and subscription</p>
      </motion.div>

      {/* Message Banner */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
            message.type === 'success'
              ? 'bg-green-500/10 border border-green-500/30'
              : 'bg-red-500/10 border border-red-500/30'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
          ) : (
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0" />
          )}
          <p className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}>
            {message.text}
          </p>
        </motion.div>
      )}

      <div className="grid gap-6 max-w-2xl">
        {/* Account Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <UserCircleIcon className="h-8 w-8 text-accent-blue" />
            <h2 className="text-xl font-semibold text-text-primary">Account Information</h2>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-border">
              <span className="text-text-secondary">Email</span>
              <span className="text-text-primary font-medium">{currentUser?.email}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-border">
              <span className="text-text-secondary">Display Name</span>
              <span className="text-text-primary font-medium">
                {userProfile?.display_name || 'Not set'}
              </span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-text-secondary">Member Since</span>
              <span className="text-text-primary font-medium">{memberSince}</span>
            </div>
          </div>
        </motion.div>

        {/* Subscription Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <CreditCardIcon className="h-8 w-8 text-accent-blue" />
            <h2 className="text-xl font-semibold text-text-primary">Subscription</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Current Plan */}
              <div className="flex justify-between items-center py-3 border-b border-border">
                <span className="text-text-secondary">Current Plan</span>
                <span className="flex items-center gap-2">
                  {isPremium ? (
                    <>
                      <span className="px-2 py-1 text-sm font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded">
                        PREMIUM
                      </span>
                    </>
                  ) : (
                    <span className="px-2 py-1 text-sm font-medium bg-gray-500/20 text-gray-400 rounded">
                      FREE
                    </span>
                  )}
                </span>
              </div>

              {/* Subscription Status */}
              {isPremium && subscriptionStatus && (
                <>
                  <div className="flex justify-between items-center py-3 border-b border-border">
                    <span className="text-text-secondary">Status</span>
                    <span className="text-green-400 font-medium">Active</span>
                  </div>

                  {userProfile?.subscription_expires_at && (
                    <div className="flex justify-between items-center py-3 border-b border-border">
                      <span className="text-text-secondary">Next Billing Date</span>
                      <span className="text-text-primary font-medium">
                        {new Date(userProfile.subscription_expires_at).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Actions */}
              <div className="pt-4">
                {isPremium ? (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="w-full py-3 px-4 rounded-lg border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors font-medium"
                  >
                    Cancel Subscription
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/dashboard?upgrade=true')}
                    className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold hover:from-amber-600 hover:to-orange-600 transition-all"
                  >
                    Upgrade to Premium
                  </button>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* Support */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-6"
        >
          <h2 className="text-xl font-semibold text-text-primary mb-4">Need Help?</h2>
          <p className="text-text-secondary mb-4">
            If you have any questions about your account or subscription, please contact our support team.
          </p>
          <a
            href="mailto:support@uknew.net"
            className="inline-flex items-center text-accent-blue hover:text-accent-blue/80 transition-colors font-medium"
          >
            support@uknew.net
          </a>
        </motion.div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card p-6 max-w-md mx-4"
          >
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
              <h3 className="text-xl font-semibold text-text-primary">Cancel Subscription?</h3>
            </div>

            <p className="text-text-secondary mb-2">
              Are you sure you want to cancel your premium subscription?
            </p>

            <ul className="text-text-secondary text-sm mb-6 space-y-2 ml-4">
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>You'll keep premium access until the end of your current billing period</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span>You can resubscribe anytime to regain access</span>
              </li>
            </ul>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={cancelLoading}
                className="flex-1 py-3 px-4 rounded-lg border border-border text-text-primary hover:bg-bg-card-hover transition-colors font-medium"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="flex-1 py-3 px-4 rounded-lg bg-red-500/20 border border-red-500/50 text-red-400 hover:bg-red-500/30 transition-colors font-medium disabled:opacity-50"
              >
                {cancelLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400"></div>
                    Cancelling...
                  </span>
                ) : (
                  'Yes, Cancel'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
